import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Field, Form as FinalForm } from 'react-final-form';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { omit } from '../../util/common';
import { useIntl, FormattedMessage } from '../../util/reactIntl';
import {
  ensureMinimumBoundsDistance,
  getBoundsCenter,
  sdkBoundsAroundCenter,
  sdkBoundsFromCoordinates,
  userLocation,
} from '../../util/maps';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
} from '../../util/search';
import {
  hasManualLocationFallbackSearch,
  isValidManualLocationFallbackSearch,
  normalizeManualLocationFallbackSearch,
} from '../../util/locationSearchFallback';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
} from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import { autocompletePlaceSelectedOrFallback } from '../../util/validators';
import {
  hasPermissionToViewData,
  isUserAuthorized,
  showCreateListingLinkForUser,
} from '../../util/userHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Form, H3, H5, LocationAutocompleteInput, NamedRedirect, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
  getDatesAndSeatsMaybe,
  getSearchPageResourceLocatorStringParams,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import SearchMap from './SearchMap/SearchMap';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import CaregiverSearchControls from './CaregiverSearchControls';

import {
  caregiverRadiusOptions,
  caregiverCategories,
  caregiverFieldQueryParamNames,
  formatMultiEnumValue,
  getServicesForCategory,
  milesToMeters,
  parseSearchRadiusMiles,
  toggleMultiEnumValue,
} from '../../util/caregiverSearch';

import css from './SearchPage.module.css';

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;
const hasActiveLocationSearch = searchParams => !!searchParams?.bounds;
const identity = value => value;

const MapSearchLocationField = ({ intl }) => (
  <Field
    name="location"
    format={identity}
    validate={autocompletePlaceSelectedOrFallback(
      intl.formatMessage({ id: 'LocationAutocompleteInput.manualFallbackValidation' })
    )}
    render={({ input, meta }) => (
      <LocationAutocompleteInput
        id="search-results-map-location"
        rootClassName={css.resultsMapLocationRoot}
        inputClassName={css.resultsMapLocationInput}
        predictionsClassName={css.resultsMapLocationPredictions}
        closeOnBlur
        showManualFallbackHint
        input={input}
        meta={meta}
        placeholder={intl.formatMessage({
          id: 'SearchPage.mapLocationPlaceholder',
          defaultMessage: 'Enter city, ZIP, or address',
        })}
        ariaLabel={intl.formatMessage({
          id: 'SearchPage.mapLocationLabel',
          defaultMessage: 'Location',
        })}
      />
    )}
  />
);

const MapSearchControls = props => {
  const { intl, address, radiusMiles, onSubmit, onUseCurrentLocation, inProgress } = props;
  const disabledTooltipMessage = intl.formatMessage({
    id: 'SearchPage.mapApplyDisabledTooltip',
    defaultMessage: 'Enter a valid ZIP or city/state to enable search.',
  });

  const initialValues = {
    radius: String(parseSearchRadiusMiles(radiusMiles)),
    location: {
      search: address || '',
      predictions: [],
      selectedPlace: null,
    },
  };

  return (
    <FinalForm
      key={`${address || ''}-${radiusMiles}`}
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ handleSubmit, invalid, values }) => {
        const hasInvalidLocationFallback =
          hasManualLocationFallbackSearch(values?.location?.search) &&
          !values?.location?.selectedPlace &&
          !isValidManualLocationFallbackSearch(values?.location?.search);

        return (
          <Form className={css.resultsMapControls} onSubmit={handleSubmit}>
            <div className={css.resultsMapActions}>
              <button
                type="button"
                className={css.resultsMapActionButton}
                onClick={onUseCurrentLocation}
                disabled={inProgress}
              >
                <FormattedMessage
                  id="SearchPage.pinCurrentLocation"
                  defaultMessage="Pin to current location"
                />
              </button>
            </div>

            <label className={css.resultsMapControlField}>
              <span className={css.resultsMapControlLabel}>
                <FormattedMessage id="SearchPage.mapLocationLabel" defaultMessage="Location" />
              </span>
              <MapSearchLocationField intl={intl} />
            </label>

            <label className={css.resultsMapControlField}>
              <span className={css.resultsMapControlLabel}>
                <FormattedMessage id="SearchPage.mapRadiusLabel" defaultMessage="Search radius" />
              </span>
              <Field name="radius" component="select" className={css.resultsMapRadiusSelect}>
                {caregiverRadiusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {intl.formatMessage({
                      id: `CaregiverSearch.radius.${option.value}`,
                      defaultMessage: option.label,
                    })}
                  </option>
                ))}
              </Field>
            </label>

            <div className={classNames(css.resultsMapControlField, css.resultsMapApplyField)}>
              <span className={css.resultsMapControlLabel} aria-hidden="true">
                &nbsp;
              </span>
              <span className={css.resultsMapApplyButtonWrapper}>
                <button type="submit" className={css.resultsMapApplyButton} disabled={invalid}>
                  <FormattedMessage
                    id="SearchPage.mapApplySearch"
                    defaultMessage="Apply area search"
                  />
                </button>
              </span>
              {hasInvalidLocationFallback ? (
                <p className={css.resultsMapApplyHelper}>{disabledTooltipMessage}</p>
              ) : null}
            </div>
          </Form>
        );
      }}
    />
  );
};

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);
    const rawSearchParams = parse(props.location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });

    this.state = {
      isAdvancedFiltersOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      isResultsMapOpen: hasActiveLocationSearch(rawSearchParams),
      highlightedListingId: null,
      activeMapListingId: null,
      currentLocationInProgress: false,
    };
    this.highlightTimeout = null;

    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);
    this.handleSortBy = this.handleSortBy.bind(this);
    this.toggleAdvancedFilters = this.toggleAdvancedFilters.bind(this);
    this.closeAdvancedFilters = this.closeAdvancedFilters.bind(this);
    this.handleCategoryChange = this.handleCategoryChange.bind(this);
    this.handleCaregiverMultiEnumFilter = this.handleCaregiverMultiEnumFilter.bind(this);
    this.handleCaregiverEnumFilter = this.handleCaregiverEnumFilter.bind(this);
    this.handleMainSearchSubmit = this.handleMainSearchSubmit.bind(this);
    this.handleMapResultSelect = this.handleMapResultSelect.bind(this);
    this.toggleResultsMap = this.toggleResultsMap.bind(this);
    this.handleUseCurrentLocation = this.handleUseCurrentLocation.bind(this);
    this.handleMapSearchSubmit = this.handleMapSearchSubmit.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.search !== this.props.location.search) {
      const rawSearchParams = parse(this.props.location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });
      this.setState({
        currentQueryParams: validUrlQueryParamsFromProps(this.props),
        isResultsMapOpen: hasActiveLocationSearch(rawSearchParams),
        highlightedListingId: null,
        activeMapListingId: null,
        currentLocationInProgress: false,
      });
    }
  }

  componentWillUnmount() {
    if (this.highlightTimeout) {
      window.clearTimeout(this.highlightTimeout);
    }
  }

  toggleAdvancedFilters() {
    this.setState(prevState => ({ isAdvancedFiltersOpen: !prevState.isAdvancedFiltersOpen }));
  }

  closeAdvancedFilters() {
    this.setState({ isAdvancedFiltersOpen: false });
  }

  // Reset all filter query parameters
  resetAll() {
    const { history, routeConfiguration, config, location } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = [
      ...getQueryParamNames(listingFieldsConfig, defaultFiltersConfig),
      ...caregiverFieldQueryParamNames,
    ];

    this.setState({ currentQueryParams: {}, isAdvancedFiltersOpen: false });

    const queryParams = omit(urlQueryParams, filterQueryParamNames);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(conf => conf.listingType);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        const datesAndSeatsMaybe = getDatesAndSeatsMaybe(mergedQueryParams, updatedURLParams);
        return {
          currentQueryParams: omitLimitedListingFieldParams(
            {
              ...mergedQueryParams,
              ...updatedURLParams,
              ...keywordsMaybe,
              ...datesAndSeatsMaybe,
              address,
              bounds,
            },
            filterConfigs
          ),
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);

          const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
            routeConfiguration,
            location
          );

          history.push(
            createResourceLocatorString(routeName, routeConfiguration, pathParams, search)
          );
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  handleCategoryChange(category) {
    const serviceCategories = category ? formatMultiEnumValue([category]) : null;

    this.getHandleChangedValueFn(true)({
      pub_category: category,
      pub_serviceCategories: serviceCategories,
      pub_services: null,
    });
  }

  handleCaregiverMultiEnumFilter(fieldKey, optionValue) {
    const currentParams = {
      ...validUrlQueryParamsFromProps(this.props),
      ...this.state.currentQueryParams,
    };
    const queryParamName = `pub_${fieldKey}`;
    const nextValue = toggleMultiEnumValue(currentParams[queryParamName], optionValue);

    this.getHandleChangedValueFn(true)({
      [queryParamName]: nextValue,
    });
  }

  handleCaregiverEnumFilter(fieldKey, value) {
    this.getHandleChangedValueFn(true)({
      [`pub_${fieldKey}`]: value,
    });
  }

  handleMainSearchSubmit(values) {
    const { history, routeConfiguration, location } = this.props;
    const { page: _page, ...currentParams } = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });
    const keywords = (values?.keywords || '').trim();

    const updatedParams = {
      ...currentParams,
      keywords: keywords || null,
    };

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, updatedParams)
    );
  }

  handleMapSearchSubmit(values) {
    const { history, routeConfiguration, location } = this.props;
    const { page: _page, ...currentParams } = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });
    const radius = String(parseSearchRadiusMiles(values?.radius || currentParams.radius));
    const locationValue = values?.location || {};
    const selectedPlace = locationValue.selectedPlace;
    const typedLocation =
      typeof locationValue.search === 'string' ? locationValue.search.trim() : '';
    const shouldClearLocation = typedLocation === '' && !!currentParams.address;

    const updatedParams = {
      ...currentParams,
      radius,
      origin: null,
    };

    if (selectedPlace) {
      updatedParams.address = selectedPlace.address || typedLocation || '';
      updatedParams.bounds = ensureMinimumBoundsDistance(
        selectedPlace.bounds,
        milesToMeters(radius)
      );
    } else if (isValidManualLocationFallbackSearch(typedLocation)) {
      updatedParams.address = normalizeManualLocationFallbackSearch(typedLocation);
      updatedParams.bounds = null;
    } else if (shouldClearLocation) {
      updatedParams.address = null;
      updatedParams.bounds = null;
    }

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, updatedParams)
    );
  }

  handleMapResultSelect(listing) {
    if (!listing?.id?.uuid || typeof document === 'undefined') {
      return;
    }

    const highlightedListingId = listing.id.uuid;
    const target = document.getElementById(`search-result-${highlightedListingId}`);

    if (this.highlightTimeout) {
      window.clearTimeout(this.highlightTimeout);
    }

    this.setState({
      highlightedListingId,
      activeMapListingId: listing.id,
    });

    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.focus({ preventScroll: true });
    }

    this.highlightTimeout = window.setTimeout(() => {
      this.setState({ highlightedListingId: null });
      this.highlightTimeout = null;
    }, 2400);
  }

  toggleResultsMap() {
    this.setState(prevState => ({
      isResultsMapOpen: !prevState.isResultsMapOpen,
    }));
  }

  handleUseCurrentLocation() {
    const { history, routeConfiguration, location, intl } = this.props;
    const currentParams = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });
    const radiusMiles = parseSearchRadiusMiles(currentParams.radius);
    const currentLocationLabel = intl.formatMessage({
      id: 'LocationAutocompleteInput.currentLocation',
    });

    this.setState({ currentLocationInProgress: true });

    userLocation()
      .then(origin => {
        const radiusBounds = sdkBoundsAroundCenter(origin, milesToMeters(radiusMiles));
        const bounds = ensureMinimumBoundsDistance(radiusBounds, milesToMeters(radiusMiles));
        const { page: _page, ...restParams } = currentParams;

        const updatedParams = {
          ...restParams,
          address: currentLocationLabel,
          bounds,
        };

        const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
          routeConfiguration,
          location
        );

        history.push(
          createResourceLocatorString(routeName, routeConfiguration, pathParams, updatedParams)
        );
      })
      .catch(() => {
        this.setState({ currentLocationInProgress: false });
      });
  }

  render() {
    const {
      intl,
      listings = [],
      location,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      routeConfiguration,
      config,
      params: currentPathParams = {},
      currentUser,
    } = this.props;

    const { listingType: listingTypePathParam } = currentPathParams;
    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersRaw, sortConfig, mainSearch } = config?.search || {};

    const activeListingTypes = config?.listing?.listingTypes.map(conf => conf.listingType);
    const defaultFiltersConfig = listingTypePathParam
      ? defaultFiltersRaw.filter(f => f.key !== 'listingType')
      : defaultFiltersRaw;

    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
      activeListingTypes,
      currentPathParams,
    });
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
      mainSearch,
    };

    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      mainSearch,
      isOriginInUse(config),
      config.maps?.search?.minimumSearchBoundsDistance
    );
    const validQueryParams = urlQueryParams;
    const rawSearchParams = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });
    const effectiveQueryParams = {
      ...validQueryParams,
      ...this.state.currentQueryParams,
    };
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === validQueryParams.dates);

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );

    const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        showCreateListingsLink={showCreateListingsLink}
      />
    );

    const sortByComponent = sortConfig.active ? (
      <SortBy
        sort={effectiveQueryParams[sortConfig.queryParamName]}
        isConflictingFilterActive={!!conflictingFilterActive}
        hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
        selectedFilters={effectiveQueryParams}
        onSelect={this.handleSortBy}
        showAsPopup
        mode="desktop"
        labelId="desktop-search-page-sort-by"
        contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
      />
    ) : null;

    const caregiverFieldConfigs = listingFieldsConfig.reduce((picked, field) => {
      return caregiverFieldQueryParamNames.includes(`pub_${field.key}`)
        ? { ...picked, [field.key]: field }
        : picked;
    }, {});

    const dateFilterConfig = defaultFiltersConfig.find(filter => filter.key === 'dates');
    const dateFilterComponent = dateFilterConfig ? (
      <FilterComponent
        id="CaregiverSearch.DateFilter"
        config={dateFilterConfig}
        containerId="CaregiverSearch_DateFilter"
        listingCategories={listingCategories}
        marketplaceCurrency={marketplaceCurrency}
        urlQueryParams={effectiveQueryParams}
        initialValues={initialValues(this.props, this.state.currentQueryParams)}
        getHandleChangedValueFn={this.getHandleChangedValueFn}
        intl={intl}
        liveEdit
        showAsPopup
      />
    ) : null;
    const servicesFieldConfig = caregiverFieldConfigs.services;
    const selectedCategory = effectiveQueryParams.pub_category || null;
    const filteredServiceOptions = getServicesForCategory(selectedCategory, servicesFieldConfig);
    const servicesFilterConfig = servicesFieldConfig
      ? {
          ...servicesFieldConfig,
          enumOptions: filteredServiceOptions.map(option => ({
            label: option.label,
            option: option.value,
          })),
          filterConfig: {
            ...(servicesFieldConfig.filterConfig || {}),
            filterType: 'SelectMultipleFilter',
            searchMode: 'has_any',
            label: 'Services Offered',
          },
        }
      : null;
    const servicesFilterComponent = servicesFilterConfig ? (
      <FilterComponent
        id="CaregiverSearch.ServicesFilter"
        config={servicesFilterConfig}
        containerId="CaregiverSearch_ServicesFilter"
        listingCategories={listingCategories}
        marketplaceCurrency={marketplaceCurrency}
        urlQueryParams={effectiveQueryParams}
        initialValues={initialValues(this.props, this.state.currentQueryParams)}
        getHandleChangedValueFn={this.getHandleChangedValueFn}
        intl={intl}
        liveEdit
        showAsPopup
      />
    ) : null;

    const pageHeading = searchInProgress
      ? intl.formatMessage({ id: 'MainPanelHeader.loadingResults' })
      : intl.formatMessage({ id: 'MainPanelHeader.foundResults' }, { count: totalItems });

    const { title, description, schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config,
      pageHeading
    );

    const topbarClasses = this.state.isAdvancedFiltersOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    const caregiverAdvancedFilterCount = caregiverFieldQueryParamNames.filter(paramName => {
      return (
        !['pub_category', 'pub_services'].includes(paramName) && !!effectiveQueryParams[paramName]
      );
    }).length;

    const activeLocationSearch = !!rawSearchParams.bounds;
    const selectedRadius = parseSearchRadiusMiles(rawSearchParams.radius);
    const radiusMiles = activeLocationSearch ? selectedRadius || 10 : null;
    const listingCoordinates = listings
      .map(listing => listing?.attributes?.geolocation)
      .filter(Boolean);
    const fallbackMapBounds = sdkBoundsFromCoordinates(listingCoordinates);
    const searchedBounds = rawSearchParams.bounds || null;
    const searchedCenter = getBoundsCenter(searchedBounds);
    const radiusBounds =
      activeLocationSearch && searchedCenter && radiusMiles
        ? sdkBoundsAroundCenter(searchedCenter, milesToMeters(radiusMiles))
        : null;
    const mapBounds = radiusBounds || searchedBounds || fallbackMapBounds;
    const mapCenter = searchedCenter || getBoundsCenter(mapBounds);
    const radiusCircle =
      activeLocationSearch && mapCenter && radiusMiles
        ? {
            center: mapCenter,
            radiusInMeters: milesToMeters(radiusMiles),
          }
        : null;
    const resultMarkerLabels = listings.reduce((picked, listing, index) => {
      return listing?.id?.uuid
        ? {
            ...picked,
            [listing.id.uuid]: intl.formatMessage(
              { id: 'SearchPage.mapResultAriaLabel' },
              { index: index + 1 }
            ),
          }
        : picked;
    }, {});
    const shouldShowMap = this.state.isResultsMapOpen && !!mapBounds;
    const mapToggleLabelId = shouldShowMap ? 'SearchPage.hideMap' : 'SearchPage.showMap';
    const mapPanelId = 'search-results-map-panel';
    const showMapSection = !!mapBounds;

    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer
          rootClassName={topbarClasses}
          currentSearchParams={validQueryParams}
          hideSearchBar
        />
        <div className={css.layoutWrapperContainer}>
          <div id="main-content" className={css.layoutWrapperMain} role="main">
            <div className={css.searchResultContainer}>
              <CaregiverSearchControls
                categories={caregiverCategories}
                servicesFilterComponent={servicesFilterComponent}
                selectedCategory={selectedCategory}
                selectedFiltersCount={caregiverAdvancedFilterCount}
                isAdvancedFiltersOpen={this.state.isAdvancedFiltersOpen}
                onSelectCategory={this.handleCategoryChange}
                onToggleAdvancedFilters={this.toggleAdvancedFilters}
                onCloseAdvancedFilters={this.closeAdvancedFilters}
                onToggleMultiEnumFilter={this.handleCaregiverMultiEnumFilter}
                onSetEnumFilter={this.handleCaregiverEnumFilter}
                onResetAll={this.resetAll}
                dateFilterComponent={dateFilterComponent}
                sortByComponent={sortByComponent}
                totalItems={totalItems}
                searchInProgress={searchInProgress}
                queryParams={effectiveQueryParams}
                fieldConfigs={caregiverFieldConfigs}
                onSubmitMainSearch={this.handleMainSearchSubmit}
              />

              {noResultsInfo}

              {showMapSection ? (
                <section className={css.resultsMapSection}>
                  <div className={css.resultsMapHeader}>
                    <div>
                      <H3 className={css.resultsMapTitle}>
                        <FormattedMessage id="SearchPage.resultsMapTitle" />
                      </H3>
                      <p className={css.resultsMapHint}>
                        <FormattedMessage id="SearchPage.resultsMapHint" />
                      </p>
                    </div>
                    <button
                      type="button"
                      className={css.resultsMapToggle}
                      onClick={this.toggleResultsMap}
                      aria-expanded={shouldShowMap}
                      aria-controls={mapPanelId}
                    >
                      <FormattedMessage id={mapToggleLabelId} />
                    </button>
                  </div>
                  {shouldShowMap ? (
                    <>
                      <MapSearchControls
                        intl={intl}
                        address={rawSearchParams.address || ''}
                        radiusMiles={selectedRadius}
                        onSubmit={this.handleMapSearchSubmit}
                        onUseCurrentLocation={this.handleUseCurrentLocation}
                        inProgress={this.state.currentLocationInProgress}
                      />
                      <div id={mapPanelId} className={css.resultsMapPanel}>
                        <SearchMap
                          id="searchResultsMap"
                          className={css.resultsMap}
                          rootClassName={css.resultsMapRoot}
                          activeListingId={this.state.activeMapListingId}
                          bounds={mapBounds}
                          center={mapCenter}
                          location={location}
                          listings={listings}
                          onMapMoveEnd={() => {}}
                          markerMode="results"
                          resultMarkerLabels={resultMarkerLabels}
                          radiusCircle={radiusCircle}
                          onResultListingClicked={this.handleMapResultSelect}
                          messages={intl.messages}
                          reuseMap={false}
                        />
                      </div>
                    </>
                  ) : null}
                </section>
              ) : null}

              <div
                className={classNames(css.listingsForGridVariant, {
                  [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                })}
              >
                {searchListingsError ? (
                  <H3 className={css.error}>
                    <FormattedMessage id="SearchPage.searchError" />
                  </H3>
                ) : null}
                {!isValidDatesFilter ? (
                  <H5>
                    <FormattedMessage id="SearchPage.invalidDatesFilter" />
                  </H5>
                ) : null}
                <SearchResultsPanel
                  className={css.searchListingsPanel}
                  listings={listings}
                  pagination={listingsAreLoaded ? pagination : null}
                  search={parse(location.search)}
                  isMapVariant={false}
                  listingTypeParam={listingTypePathParam}
                  intl={intl}
                  highlightedListingId={this.state.highlightedListingId}
                />
              </div>
            </div>
          </div>
        </div>
        <FooterContainer />
      </Page>
    );
  }
}

/**
 * SearchPage component with grid layout (no map)
 *
 * @param {Object} props
 * @param {propTypes.currentUser} [props.currentUser] - The current user
 * @param {Array<propTypes.listing>} [props.listings] - The listings
 * @param {propTypes.pagination} [props.pagination] - The pagination
 * @param {boolean} [props.scrollingDisabled] - Whether the scrolling is disabled
 * @param {boolean} [props.searchInProgress] - Whether the search is in progress
 * @param {propTypes.error} [props.searchListingsError] - The search listings error
 * @param {Object} [props.searchParams] - The search params from the Redux state
 * @returns {JSX.Element}
 */
const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const searchListingsError = props.searchListingsError;
  if (isForbiddenError(searchListingsError)) {
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
  const hasNoViewingRightsError = isErrorNoViewingPermission(searchListingsError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (hasNoViewingRightsUser || hasNoViewingRightsError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      currentUser={currentUser}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);

  return {
    currentUser,
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
  };
};

const SearchPage = compose(connect(mapStateToProps))(EnhancedSearchPage);

export default SearchPage;
